import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core'
import { DatePipe, UpperCasePipe } from '@angular/common'
import { MockDataService } from '@appShared/services'
import { ISubscription, ISubscriptionConversionOption } from '@appShared/interfaces/[Model-based]'
import { SubscriptionStatus, SubscriptionStatus_ } from '@appShared/services/lookup/[CodeGen]'

@Component({
   selector: 'app-account-card',
   templateUrl: './account-card.component.html',
   imports: [DatePipe, UpperCasePipe],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountCardComponent {
   public readonly actionableSubscription = input.required<ISubscription>()
   /** When true (default) the owner email renders as a `mailto:` link; pass false
       to show it as plain text (e.g. in the account-search results list). */
   public readonly emailAsLink = input<boolean>(true)

   private readonly _mockDataService = inject(MockDataService)
   public readonly partnerRepresentative = computed(() => this.actionableSubscription()?.partnerRepresentative)
   private readonly relevantSubscriptionAuthorization = computed(() => this.actionableSubscription()?.info?.relevantSubscriptionAuthorization)
   private readonly relevantSubscriptionAuthorizationCurrency = computed(() => this.relevantSubscriptionAuthorization()?.paymentInstrument?.info?.currencyShortDescription || '$')
   public readonly plannedSubscriptionConversionOption = computed(() => {
      let plannedOption = this.actionableSubscription()?.subscriptionConversionOptions?.find((option) => option.isPlanned)

      if (!plannedOption) {
         return undefined
      }

      return {
         ...plannedOption,
         productSummary: plannedOption.subscriptionAuthorizationAmount != null
            ? `${plannedOption.productDisplayName}: ${this.relevantSubscriptionAuthorizationCurrency()}${plannedOption.subscriptionAuthorizationAmount}`
            : plannedOption.productDisplayName
      } as ISubscriptionConversionOption
   })
   public readonly nextPaymentDue = computed(() => this.actionableSubscription()?.nextPaymentDue)

   public readonly seatLimit = computed(() => this.actionableSubscription()?.seatLimit)
   public readonly seatsUsed = computed(() => this.actionableSubscription()?.subscriptionSeats?.length ?? 0)
   /* A default seat limit of exactly 1 marks a single-seat product */
   public readonly isMultiSeatProduct = computed(() => {
      const product = this.actionableSubscription()?.product
      return !!product && product.defaultSeatLimit !== 1
   })

   public readonly productSummary = computed(() => {
      const sub = this.actionableSubscription()
      const productName = sub?.product?.displayName ?? ''
      const subAuth = this.relevantSubscriptionAuthorization()      
      const currency = this.relevantSubscriptionAuthorizationCurrency()
      const amount = subAuth?.amount
      return amount != null ? `${productName}: ${currency}${amount}` : productName
   })

   public readonly accountOwnerProfileContact = computed(() =>
      this.actionableSubscription().info?.accountOwnerProfile?.contact
   )

   public readonly statusDescription = computed(() =>
      SubscriptionStatus.toDomainModel(this.actionableSubscription().statusCode)?.description
   )

   public readonly statusClass = computed(() => {
      switch (this.actionableSubscription().statusCode) {
         case SubscriptionStatus_.Active:
            return 'subscription-status-active'
         case SubscriptionStatus_.Cancelled:
         case SubscriptionStatus_.Ended:
            return 'subscription-status-danger'
         case SubscriptionStatus_.Renewal:
            return 'subscription-status-info'
         case SubscriptionStatus_.OnHold:
         case SubscriptionStatus_.Paused:
            return 'subscription-status-warning'
         default:
            return ''
      }
   })
}
